// src/realtime/transports.ts
import { createSseParser } from "./sse-parser";
import type { Frame, Transport } from "./types";

type Env = { baseUrl: string; fetchImpl?: typeof fetch; wsImpl?: typeof WebSocket };

/** SSE عبر fetch المتدفق (بدل EventSource): يعمل في المتصفح وNode ويتيح الإلغاء وقراءة كود الحالة. */
export function sseTransport({ baseUrl, fetchImpl }: Env): Transport {
  return {
    open(ticket, { onFrame, onClose }) {
      const ctrl = new AbortController();
      const doFetch = fetchImpl ?? fetch;
      (async () => {
        try {
          const res = await doFetch(`${baseUrl}/api/stream/events?ticket=${encodeURIComponent(ticket)}`, {
            headers: { Accept: "text/event-stream" },
            signal: ctrl.signal,
            cache: "no-store",
          });
          if (!res.ok || !res.body) return onClose({ status: res.status });
          const parse = createSseParser(onFrame);
          const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            parse(value);
          }
          onClose({});
        } catch {
          if (!ctrl.signal.aborted) onClose({ error: true });
        }
      })();
      return { close: () => ctrl.abort() };
    },
  };
}

function wsUrl(baseUrl: string, ticket: string): string {
  const origin = baseUrl || (typeof location !== "undefined" ? location.origin : "");
  return `${origin.replace(/^http/, "ws")}/api/ws?ticket=${encodeURIComponent(ticket)}`;
}

/** WebSocket: نفس تدفق الإطارات، والإغلاق بكود 4xxx يحمل سبب الرفض. */
export function wsTransport({ baseUrl, wsImpl }: Env): Transport {
  return {
    open(ticket, { onFrame, onClose }) {
      const Impl = wsImpl ?? WebSocket;
      const ws = new Impl(wsUrl(baseUrl, ticket));
      let closedByUs = false;
      ws.onmessage = (m) => {
        try {
          const msg = JSON.parse(String(m.data));
          if (msg.type === "pong") return;
          onFrame({ kind: msg.type, data: msg.type === "event" ? msg.event : (msg.data ?? {}) } as Frame);
        } catch {
          /* رسالة غير صالحة — تُتجاهل */
        }
      };
      ws.onclose = (e) => !closedByUs && onClose({ code: e.code });
      ws.onerror = () => undefined; // الإغلاق بعده يحمل التفاصيل
      return {
        close: () => {
          closedByUs = true;
          ws.close();
        },
      };
    },
  };
}
